import ModalRequest from '../../components/ModalRequest'
import { useAuth } from '../../hook/useAuth'
import {
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import axiosInstance from '../../config/axiosConfig'
import { urlConfig } from '../../config/apiConfig'
import dayjs from 'dayjs'
import { DatePicker } from '@mui/x-date-pickers'
import { AlertContext } from '../../context/AlertContext'
import ModalResetPw from './ModalResetPw'

export default function Edit({
    open,
    setOpen,
    username,
    handleAlert,
    getUserByKey,
    row,
}) {
    const [role, setRole] = useState([])
    const [loading, setLoading] = useState(false)
    const [site, setSite] = useState('')
    const { auth } = useAuth()
    const myLevel = auth?.role_level || 0
    const { handleSubmit, setValue, watch, register, reset } = useForm()
    const [resetPw, setResetPw] = useState(false)
    const [data, setData] = useState('')

    const getUser = useCallback(async () => {
        try {
            const res = await axiosInstance.get(
                `${urlConfig.user}/user_id/${row.user_id}`,
            )
            setData(res.data.message)
        } catch (err) {
            console.error(err)
        } finally {
        }
    }, [row])

    useEffect(() => {
        getUser()
    }, [row, getUser])

    const onSubmit = async (data) => {
        if (data.password !== data.confirmPassword) {
            alert('Passwords do not match!')
            return
        }
        const body = {
            ...data,
            username: username,
            expire_date: dayjs(data.expire_date, 'DD/MM/YYYY').format(
                'YYYY-MM-DD',
            ),
        }
        if (!data.phone_number) {
            delete body.phone_number
        }
        delete body.set_new_pwd
        try {
            setLoading(true)
            await axiosInstance.put(`${urlConfig.user}/${row.user_id}`, body)
            handleAlert('User updated successfully.', 'success')
            getUserByKey()
            setOpen(false)
        } catch (err) {
            handleAlert(
                `${err.response.data.message || 'No Internet'}`,
                'error',
            )
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (row) {
            const formattedRow = {
                ...row,
                expire_date: row.expire_date
                    ? dayjs(row.expire_date).format('DD/MM/YYYY')
                    : '',
            }
            reset(formattedRow)
        }
    }, [row, reset])

    const getRole = useCallback(async () => {
        if (!myLevel) return
        try {
            const res = await axiosInstance.get(urlConfig.role)
            if (!res.data.message[0]) return
            const filteredRoles = res.data?.message?.filter(
                (item) => item.level <= myLevel,
            )
            setRole(filteredRoles)
        } catch (error) {
            handleOpenRef.current({
                open: true,
                message: `${error.response?.data.message}`,
                isSuccess: false,
            })
        }
    }, [myLevel])

    useEffect(() => {
        getRole()
    }, [getRole])

    const { handleOpen: contextHandleOpen } = useContext(AlertContext)
    const handleOpenRef = useRef(contextHandleOpen)

    useEffect(() => {
        handleOpenRef.current = contextHandleOpen
    }, [contextHandleOpen])

    const getSite = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`${urlConfig.organization}`)
            if (res.data.message.length > 0) {
                setSite(res.data.message)
                handleOpenRef.current({ open: false })
            }
        } catch (error) {
            handleOpenRef.current({
                open: true,
                message: `${error.response?.data.message}`,
                isSuccess: false,
            })
        }
    }, [setSite])

    useEffect(() => {
        getSite()
    }, [getSite])

    const handleChange = (name, value) => {
        name === 'keyName'
            ? setValue(name, value)
            : setValue(name, value ? 'Active' : 'Inactive')
    }

    return (
        <>
            <ModalResetPw
                isCancel={(data && data.set_new_pwd) || false}
                handleAlert={handleAlert}
                open={resetPw}
                setOpen={setResetPw}
                row={row}
                getUser={getUser}
                setResetPw={setResetPw}
            />
            <ModalRequest
                open={open}
                disabled={loading}
                setOpen={setOpen}
                title={`Edit User [${username}]`}
                onSubmit={handleSubmit(onSubmit)}
            >
                <Grid item xs={12}>
                    <FormControl
                        fullWidth
                        size='small'
                        sx={{
                            height: '35px',
                        }}
                    >
                        <InputLabel id='group-select-label'>Group</InputLabel>
                        <Select
                            labelId='group-select-label'
                            label='Group'
                            id='group-select'
                            required
                            onChange={(e) => {
                                setValue('role_id', null)
                                setValue('group', e.target.value)
                            }}
                            value={
                                ['User', 'Admin'].includes(watch('group')) // Ensure value is valid
                                    ? watch('group')
                                    : ''
                            }
                        >
                            <MenuItem value={'User'}>User</MenuItem>
                            <MenuItem value={'Admin'}>Admin</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {watch('group') && role && (
                    <Grid item xs={12}>
                        <FormControl
                            fullWidth
                            size='small'
                            sx={{
                                height: '35px',
                            }}
                        >
                            <InputLabel id='role-select-label'>Role</InputLabel>
                            <Select
                                labelId='role-select-label'
                                label='Role'
                                id='role-select'
                                required
                                onChange={(e) =>
                                    setValue('role_id', e.target.value)
                                }
                                value={
                                    role.some(
                                        (item) =>
                                            item.role_id === watch('role_id'),
                                    ) // Ensure value is valid
                                        ? watch('role_id')
                                        : ''
                                }
                            >
                                <MenuItem value={''}>-</MenuItem>
                                {role &&
                                    role
                                        .filter(
                                            (item) =>
                                                item.group === watch('group'),
                                        )
                                        .map((item, index) => (
                                            <MenuItem
                                                key={index}
                                                value={item.role_id}
                                            >
                                                {item.name}
                                            </MenuItem>
                                        ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                {site && watch('keyName') && (
                    <Grid item xs={12}>
                        <FormControl
                            fullWidth
                            size='small'
                            sx={{
                                height: '35px',
                            }}
                        >
                            <InputLabel id='organization-select-label'>
                                Organization
                            </InputLabel>
                            <Select
                                labelId='organization-select-label'
                                label='Organization'
                                id='organization-select'
                                required
                                onChange={(e) =>
                                    handleChange('keyName', e.target.value)
                                }
                                value={
                                    site.some(
                                        (item) =>
                                            item.keyName === watch('keyName'),
                                    ) // Ensure value is valid
                                        ? watch('keyName')
                                        : ''
                                }
                                disabled={true}
                            >
                                {site &&
                                    site.map((item, index) => (
                                        <MenuItem
                                            key={index}
                                            value={item.keyName}
                                        >
                                            {item.site_name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                <Grid item xs={12} md={12}>
                    <TextField
                        fullWidth
                        label='Username'
                        size='small'
                        defaultValue={watch('username')}
                        {...register('username')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        required
                        fullWidth
                        label='First Name'
                        size='small'
                        defaultValue={watch('first_name')}
                        {...register('first_name')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        required
                        fullWidth
                        label='Last Name'
                        size='small'
                        defaultValue={watch('last_name')}
                        {...register('last_name')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        required
                        fullWidth
                        label='Email'
                        size='small'
                        type='email'
                        defaultValue={watch('email')}
                        {...register('email')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label='Phone Number'
                        size='small'
                        defaultValue={watch('phone_number')}
                        {...register('phone_number')}
                    />
                </Grid>
                <Grid item xs={12} md={12}>
                    <DatePicker
                        sx={{ width: '100%' }}
                        name='expire_date'
                        label='Expire Date'
                        InputLabelProps={{ shrink: true }}
                        slotProps={{ textField: { size: 'small' } }}
                        value={
                            watch('expire_date')
                                ? dayjs(watch('expire_date'), 'DD/MM/YYYY')
                                : null
                        }
                        format='DD/MM/YYYY'
                        onChange={(date) => {
                            setValue(
                                'expire_date',
                                date ? date.format('DD/MM/YYYY') : '',
                            )
                        }}
                    />
                </Grid>
                <Grid item xs={12} align='right'>
                    <Box>
                        <label>Active</label>
                        <Switch
                            checked={watch('status') === 'Active'}
                            color='success'
                            onChange={(e, nv) => handleChange('status', nv)}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} align='right'>
                    <Box>
                        <label>Reset Password</label>
                        <Switch
                            checked={
                                data && data.set_new_pwd
                                    ? data.set_new_pwd
                                    : resetPw
                            }
                            color='success'
                            onChange={(e, nv) =>
                                data && data.set_new_pwd
                                    ? setResetPw(true)
                                    : setResetPw(nv)
                            }
                        />
                    </Box>
                </Grid>
            </ModalRequest>
        </>
    )
}
